import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import { Color, MathUtils, PerspectiveCamera, Vector3 } from "three";
import { games, homeHeroMedia } from "../content/siteContent";
import { getViewportTier, type ViewportTier } from "../lib/viewport";
import { useAppStore } from "../store/useAppStore";
import { useProjectsCarouselStore } from "../store/useProjectsCarouselStore";
import { useSectionEntryStore } from "../store/useSectionEntryStore";
import {
  blendNumber,
  clamp01,
  easeInOutCubic,
  getSectionCameraPose,
  getStudioToProjectsDollyProgress,
  sectionBackgrounds,
  studioToProjectsDollyEnd
} from "./transitions/sectionMotion";
import { getStudioImageFocalPoint } from "./sections/studioSceneLayout";
import { getAboutSceneLayout } from "./sections/aboutSceneLayout";

const nextPosition = new Vector3();
const nextTarget = new Vector3();
const blendedTarget = new Vector3();
const bridgePosition = new Vector3();
const bridgeTarget = new Vector3();
const releasePosition = new Vector3();
const releaseTarget = new Vector3();
const bridgePositionMid = new Vector3();
const bridgeTargetMid = new Vector3();
const aboutOrbitPosition = new Vector3();
const aboutOrbitTarget = new Vector3();
const aboutStackCenter = new Vector3();
const studioHoverPosition = new Vector3(3.15, 0.48, 6.2);
const studioHoverTarget = new Vector3(0.45, 0.12, -0.15);
const studioPortalFocus = {
  desktop: getStudioImageFocalPoint(homeHeroMedia.focalPoint, -0.38, "desktop"),
  mobile: getStudioImageFocalPoint(homeHeroMedia.focalPoint, -0.38, "mobile")
};
const studioPortalPosition = {
  desktop: new Vector3(studioPortalFocus.desktop[0] - 0.24, studioPortalFocus.desktop[1] + 0.08, 1.65),
  mobile: new Vector3(studioPortalFocus.mobile[0] - 0.08, studioPortalFocus.mobile[1] + 0.12, 1.85)
};
const studioPortalTarget = {
  desktop: new Vector3(studioPortalFocus.desktop[0], studioPortalFocus.desktop[1], studioPortalFocus.desktop[2]),
  mobile: new Vector3(studioPortalFocus.mobile[0], studioPortalFocus.mobile[1], studioPortalFocus.mobile[2])
};
const studioPortalExitPosition = {
  desktop: new Vector3(0.65, 0.34, 4.15),
  mobile: new Vector3(0.02, 0.42, 4.35)
};
const studioPortalExitTarget = {
  desktop: new Vector3(0.4, 0.12, -0.95),
  mobile: new Vector3(0, 0.06, -0.95)
};

function getDetailBackground(slug: string | null) {
  const entry = games.find((game) => game.slug === slug);
  return new Color(entry?.palette.secondary ?? "#f3ebe1");
}

function getAboutOrbitConfig(viewport: ViewportTier) {
  if (viewport === "mobile") {
    return {
      targetYOffset: 0.16,
      fov: 34,
      smoothing: 8.2
    };
  }

  if (viewport === "tablet") {
    return {
      targetYOffset: 0.18,
      fov: 35,
      smoothing: 7.8
    };
  }

  return {
    targetYOffset: 0.2,
    fov: 36.5,
    smoothing: 7.4
  };
}

export function CameraRig() {
  const camera = useThree((state) => state.camera as PerspectiveCamera);
  const scene = useThree((state) => state.scene);
  const viewport = useThree((state) => getViewportTier(state.size.width));
  const currentSection = useAppStore((state) => state.currentSection);
  const targetSection = useAppStore((state) => state.targetSection);
  const transitionProgress = useAppStore((state) => state.transitionProgress);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const reducedMotion = useAppStore((state) => state.reducedMotion);
  const experienceMode = useAppStore((state) => state.experienceMode);
  const activeGameSlug = useAppStore((state) => state.activeGameSlug);
  const studioHovered = useAppStore((state) => state.studioHovered);
  const teamHovered = useAppStore((state) => state.teamHovered);
  const teamPointer = useAppStore((state) => state.teamPointer);
  const activeProjectIndex = useProjectsCarouselStore((state) => state.activeIndex);
  const teamEntryState = useSectionEntryStore((state) => state.sections.team);
  const lookAtRef = useRef(new Vector3(0, 0, 0));
  const studioPortalViewport = viewport === "mobile" ? "mobile" : "desktop";

  const background = useMemo(() => new Color(sectionBackgrounds.studio), []);

  useFrame((state, delta) => {
    let aboutCameraInfluence = 0;
    let aboutCameraSmoothing = 0;

    if (experienceMode === "detail") {
      nextPosition.set(0.24, 0.18, 5.4);
      nextTarget.set(0, 0.05, -0.4);
      background.copy(new Color("#f8f2ea")).lerp(getDetailBackground(activeGameSlug), 0.3);
      camera.fov = blendNumber(camera.fov, 33, 1 - Math.exp(-delta * 5));
    } else {
      const fromPose = getSectionCameraPose(currentSection, activeProjectIndex, viewport);
      const toPose = getSectionCameraPose(
        isTransitioning ? targetSection : currentSection,
        activeProjectIndex,
        viewport
      );
      const progress = isTransitioning ? transitionProgress : 1;
      const isStudioProjectsTransition = currentSection === "studio" && targetSection === "projects" && isTransitioning && !reducedMotion;

      if (isStudioProjectsTransition) {
        const dollyProgress = getStudioToProjectsDollyProgress(progress);
        const emergeProgress = easeInOutCubic(clamp01((progress - studioToProjectsDollyEnd) / (1 - studioToProjectsDollyEnd)));
        const studioPortalPositionForViewport = studioPortalPosition[studioPortalViewport];
        const studioPortalTargetForViewport = studioPortalTarget[studioPortalViewport];
        const studioPortalExitPositionForViewport = studioPortalExitPosition[studioPortalViewport];
        const studioPortalExitTargetForViewport = studioPortalExitTarget[studioPortalViewport];
        bridgePosition.set(
          blendNumber(fromPose.position[0], studioPortalPositionForViewport.x, dollyProgress),
          blendNumber(fromPose.position[1], studioPortalPositionForViewport.y, dollyProgress),
          blendNumber(fromPose.position[2], studioPortalPositionForViewport.z, dollyProgress)
        );
        bridgeTarget.set(
          blendNumber(fromPose.target[0], studioPortalTargetForViewport.x, dollyProgress),
          blendNumber(fromPose.target[1], studioPortalTargetForViewport.y, dollyProgress),
          blendNumber(fromPose.target[2], studioPortalTargetForViewport.z, dollyProgress)
        );
        bridgePositionMid.set(
          blendNumber(studioPortalPositionForViewport.x, studioPortalExitPositionForViewport.x, emergeProgress),
          blendNumber(studioPortalPositionForViewport.y, studioPortalExitPositionForViewport.y, emergeProgress),
          blendNumber(studioPortalPositionForViewport.z, studioPortalExitPositionForViewport.z, emergeProgress)
        );
        bridgeTargetMid.set(
          blendNumber(studioPortalTargetForViewport.x, studioPortalExitTargetForViewport.x, emergeProgress),
          blendNumber(studioPortalTargetForViewport.y, studioPortalExitTargetForViewport.y, emergeProgress),
          blendNumber(studioPortalTargetForViewport.z, studioPortalExitTargetForViewport.z, emergeProgress)
        );
        releasePosition.set(toPose.position[0], toPose.position[1], toPose.position[2]);
        releaseTarget.set(toPose.target[0], toPose.target[1], toPose.target[2]);

        nextPosition.copy(bridgePosition).lerp(bridgePositionMid, emergeProgress).lerp(releasePosition, emergeProgress * 0.9);
        nextTarget.copy(bridgeTarget).lerp(bridgeTargetMid, emergeProgress).lerp(releaseTarget, emergeProgress * 0.92);
        camera.fov = blendNumber(
          camera.fov,
          blendNumber(blendNumber(fromPose.fov, 28.5, dollyProgress), toPose.fov, emergeProgress),
          1 - Math.exp(-delta * 5)
        );
      } else {
        nextPosition.set(
          blendNumber(fromPose.position[0], toPose.position[0], progress),
          blendNumber(fromPose.position[1], toPose.position[1], progress),
          blendNumber(fromPose.position[2], toPose.position[2], progress)
        );
        nextTarget.set(
          blendNumber(fromPose.target[0], toPose.target[0], progress),
          blendNumber(fromPose.target[1], toPose.target[1], progress),
          blendNumber(fromPose.target[2], toPose.target[2], progress)
        );
      }

      const fromBackground = sectionBackgrounds[currentSection];
      const toBackground = sectionBackgrounds[isTransitioning ? targetSection : currentSection];
      if (isStudioProjectsTransition) {
        const backgroundProgress = clamp01((progress - 0.52) / 0.48);
        background.copy(fromBackground).lerp(toBackground, backgroundProgress);
      } else {
        background.copy(fromBackground).lerp(toBackground, progress);
        camera.fov = blendNumber(camera.fov, blendNumber(fromPose.fov, toPose.fov, progress), 1 - Math.exp(-delta * 5));
      }

      if (viewport !== "mobile" && !reducedMotion && currentSection === "studio" && !isTransitioning && studioHovered) {
        nextPosition.lerp(studioHoverPosition, 0.72);
        nextTarget.lerp(studioHoverTarget, 0.72);
        camera.fov = blendNumber(camera.fov, 34, 1 - Math.exp(-delta * 4));
      }

      if (!reducedMotion && currentSection === "team" && !isTransitioning) {
        const landingX = blendNumber(-0.24, 0.02, teamEntryState.entryProgress);
        const landingY = blendNumber(0.42, 0.08, teamEntryState.entryProgress);
        const landingZ = blendNumber(0.88, 0.14, teamEntryState.entryProgress);
        const browseX = blendNumber(0, 0.22, teamEntryState.browseProgress);
        const browseY = blendNumber(0, -0.06, teamEntryState.browseProgress);
        const browseZ = blendNumber(0, -0.36, teamEntryState.browseProgress);
        const idleX = Math.sin(state.clock.elapsedTime * 0.18) * 0.08;
        const idleY = Math.cos(state.clock.elapsedTime * 0.22) * 0.05;
        const idleZ = Math.sin(state.clock.elapsedTime * 0.14) * 0.05;
        const pointerX = teamPointer.x;
        const pointerY = teamPointer.y;
        const baseParallaxX = pointerX * -0.05;
        const baseParallaxY = pointerY * -0.03;

        nextPosition.x += landingX + browseX + idleX + baseParallaxX;
        nextPosition.y += landingY + browseY + idleY + baseParallaxY;
        nextPosition.z += landingZ + browseZ + idleZ;
        nextTarget.x += blendNumber(-0.14, 0.16, teamEntryState.browseProgress) + pointerX * -0.18;
        nextTarget.y +=
          blendNumber(0.2, -0.08, teamEntryState.entryProgress * 0.65 + teamEntryState.browseProgress * 0.35) +
          pointerY * -0.12;

        if (teamHovered) {
          const hoverDiagonal = pointerX * -0.24;
          nextPosition.x += hoverDiagonal;
          nextPosition.y += hoverDiagonal * 0.18 + pointerY * -0.05;
          nextPosition.z += -0.18 + Math.abs(hoverDiagonal) * -0.08 + pointerY * 0.02;
          nextTarget.x += pointerX * -0.3;
          nextTarget.y += hoverDiagonal * 0.08 + pointerY * -0.14;
          camera.fov = blendNumber(camera.fov, 32.6, 1 - Math.exp(-delta * 4));
        }
      }

      const isEnteringAbout = isTransitioning && targetSection === "about" && currentSection !== "about";
      const isLeavingAbout = isTransitioning && currentSection === "about" && targetSection !== "about";
      const shouldRunAboutOrbit = !reducedMotion && !isStudioProjectsTransition && (isEnteringAbout || isLeavingAbout);

      if (shouldRunAboutOrbit) {
        const aboutPose = getSectionCameraPose("about", activeProjectIndex, viewport);
        const aboutLayout = getAboutSceneLayout(viewport);
        const aboutOrbitConfig = getAboutOrbitConfig(viewport);
        const orbitProgress = MathUtils.smoothstep(progress, 0, 1);
        const pointerX = state.pointer.x;
        const pointerY = state.pointer.y;

        aboutStackCenter.set(
          aboutLayout.basePosition[0] + aboutLayout.stackPosition[0] * aboutLayout.baseScale,
          aboutLayout.basePosition[1] + aboutLayout.stackPosition[1] * aboutLayout.baseScale + aboutOrbitConfig.targetYOffset,
          aboutLayout.basePosition[2] + aboutLayout.stackPosition[2] * aboutLayout.baseScale
        );
        const baseDeltaX = aboutPose.position[0] - aboutStackCenter.x;
        const baseDeltaZ = aboutPose.position[2] - aboutStackCenter.z;
        const baseAngle = Math.atan2(baseDeltaX, baseDeltaZ);
        const stableRadius = Math.hypot(baseDeltaX, baseDeltaZ);
        const orbitDirection = isEnteringAbout ? -1 : 1;
        const angle = baseAngle + orbitDirection * Math.PI * 2 * orbitProgress + pointerX * 0.025;

        aboutOrbitTarget.set(
          aboutStackCenter.x + pointerX * 0.025,
          aboutStackCenter.y + pointerY * 0.018,
          aboutStackCenter.z
        );
        aboutOrbitPosition.set(
          aboutStackCenter.x + Math.sin(angle) * stableRadius,
          aboutPose.position[1] + pointerY * 0.018,
          aboutStackCenter.z + Math.cos(angle) * stableRadius
        );

        nextPosition.copy(aboutOrbitPosition);
        nextTarget.copy(aboutOrbitTarget);
        camera.fov = blendNumber(camera.fov, aboutOrbitConfig.fov, 1 - Math.exp(-delta * 5));
        aboutCameraInfluence = 1;
        aboutCameraSmoothing = aboutOrbitConfig.smoothing;
      }

    }

    const isStudioProjectsTransition = currentSection === "studio" && targetSection === "projects" && isTransitioning && !reducedMotion;
    const positionSmoothing = isStudioProjectsTransition
      ? 7.8
      : aboutCameraInfluence > 0.001
        ? blendNumber(reducedMotion ? 6.5 : 3.5, aboutCameraSmoothing, aboutCameraInfluence)
        : reducedMotion
          ? 6.5
          : 3.5;
    const targetSmoothing = isStudioProjectsTransition
      ? 8.6
      : aboutCameraInfluence > 0.001
        ? blendNumber(reducedMotion ? 7 : 4.2, aboutCameraSmoothing + 0.35, aboutCameraInfluence)
        : reducedMotion
          ? 7
          : 4.2;
    camera.position.lerp(nextPosition, 1 - Math.exp(-delta * positionSmoothing));
    lookAtRef.current.lerp(nextTarget, 1 - Math.exp(-delta * targetSmoothing));
    blendedTarget.copy(lookAtRef.current);
    camera.lookAt(blendedTarget);
    camera.updateProjectionMatrix();
    scene.background = background;
    if (scene.fog) {
      scene.fog.color.copy(background);
    }
  });

  return null;
}
